import { useEffect, useState } from "react";
import { api, formatINR, formatINRShort, formatApiError } from "@/lib/api";
import { useLiveRefresh } from "@/lib/live";
import { PageHeader } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Building2, ChevronDown, ChevronRight, Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { FinanceLock } from "@/components/FinanceLock";

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => setCustomers((c) => c || []));
  useEffect(() => { load(); }, []);
  useLiveRefresh("customers", load);

  const filteredMaster = q
    ? customers.filter((c) =>
        (c.party_name || "").toLowerCase().includes(q.toLowerCase()) ||
        (c.party_code || "").toLowerCase().includes(q.toLowerCase())
      )
    : customers;

  return (
    <div>
      <PageHeader
        subtitle="Party Master"
        title="Customers"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-customer-btn" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
                <Plus size={16} className="mr-1.5" />Add Customer
              </Button>
            </DialogTrigger>
            <CustomerForm onDone={() => { setOpen(false); load(); }} />
          </Dialog>
        }
      />
      <div className="p-8">
        <div className="mb-4">
          <Input
            data-testid="customer-search"
            placeholder="Search customer / party name / code…"
            className="rounded-sm w-full max-w-96"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        <Tabs defaultValue="master" className="w-full">
          <TabsList className="rounded-sm bg-white border border-neutral-200">
            <TabsTrigger data-testid="tab-master" value="master" className="rounded-sm">Master List</TabsTrigger>
            <TabsTrigger data-testid="tab-groups" value="groups" className="rounded-sm">By Revenue</TabsTrigger>
          </TabsList>

          {editing && (
            <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
              <CustomerForm initial={editing} onDone={() => { setEditing(null); load(); }} />
            </Dialog>
          )}

          <TabsContent value="master" className="mt-4">
            <div className="border border-neutral-200 bg-white overflow-x-auto">
              <table className="w-full text-sm" data-testid="customer-master-table">
                <thead className="bg-neutral-50 label-caps">
                  <tr>
                    <th className="text-left px-4 py-2.5">Party Name</th>
                    <th className="text-left px-4 py-2.5">Code / Unit</th>
                    <th className="text-left px-4 py-2.5">Location</th>
                    <th className="text-left px-4 py-2.5">GSTIN</th>
                    <th className="text-left px-4 py-2.5">Contact</th>
                    <th className="text-left px-4 py-2.5">Phone</th>
                    <th className="text-right px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMaster.map((c) => (
                    <tr key={c.id || `${c.party_name}-${c.party_code}`} className="border-t border-neutral-100 data-row">
                      <td className="px-4 py-2.5 font-medium">{c.party_name}</td>
                      <td className="px-4 py-2.5 font-mono-num text-xs text-neutral-600">{c.party_code || "—"}</td>
                      <td className="px-4 py-2.5 text-neutral-600">{c.location || "—"}</td>
                      <td className="px-4 py-2.5 font-mono-num text-xs">{c.gstin || "—"}</td>
                      <td className="px-4 py-2.5">{c.contact_person || "—"}</td>
                      <td className="px-4 py-2.5 font-mono-num text-xs">{c.phone || "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex justify-end gap-2">
                        {c.id && (
                          <button
                            onClick={() => setEditing(c)}
                            data-testid={`edit-customer-${c.id}`}
                            className="text-neutral-400 hover:text-[#FF5A00] transition-colors"
                            title="Edit customer"
                          >
                            <Pencil size={14} />
                          </button>
                        )}
                        {c.id && <DeleteBtn id={c.id} name={c.party_name} onDone={load} />}
                      </div>
                    </td>
                    </tr>
                  ))}
                  {filteredMaster.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-neutral-400">
                      No customers yet. Click <span className="text-[#FF5A00] font-medium">Add Customer</span> to create your first party.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-2 text-xs text-neutral-500">
              <span className="font-mono-num font-bold text-neutral-900">{filteredMaster.length}</span> customers in master
            </div>
          </TabsContent>

          <TabsContent value="groups" className="mt-4">
            <FinanceLock title="Customer Revenue">
              <RevenueViewLoader defaultQ={q} />
            </FinanceLock>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function DeleteBtn({ id, name, onDone }) {
  const del = async () => {
    if (!window.confirm(`Delete customer "${name}"? This cannot be undone.`)) return;
    try { await api.delete(`/customers/${id}`); toast.success("Deleted"); onDone(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <button data-testid={`del-customer-${id}`} onClick={del} className="text-neutral-400 hover:text-rose-700 transition-colors">
      <Trash2 size={14} />
    </button>
  );
}

function CustomerForm({ initial, onDone }) {
  const isEdit = !!initial?.id;
  const [f, setF] = useState({
    party_name: initial?.party_name || "",
    party_code: initial?.party_code || "",
    gstin: initial?.gstin || "",
    location: initial?.location || "",
    contact_person: initial?.contact_person || "",
    phone: initial?.phone || "",
    email: initial?.email || "",
    corporate_address: initial?.corporate_address || initial?.address || "",
    delivery_address: initial?.delivery_address || "",
    notes: initial?.notes || "",
  });
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) {
        await api.patch(`/customers/${initial.id}`, f);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", f);
        toast.success("Customer added");
      }
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-display">{isEdit ? "Edit Customer" : "New Customer"}</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="label-caps block mb-1.5">Party Name *</label>
          <Input data-testid="new-cust-name" value={f.party_name} onChange={(e) => setF({ ...f, party_name: e.target.value })} className="rounded-sm" required />
        </div>
        <div>
          <label className="label-caps block mb-1.5">Party Code / Unit</label>
          <Input data-testid="new-cust-code" placeholder="e.g. GP-PSM" value={f.party_code} onChange={(e) => setF({ ...f, party_code: e.target.value })} className="rounded-sm" />
        </div>
        <div>
          <label className="label-caps block mb-1.5">Location (short)</label>
          <Input data-testid="new-cust-location" placeholder="e.g. Hyderabad" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className="rounded-sm" />
        </div>
        <div>
          <label className="label-caps block mb-1.5">GSTIN</label>
          <Input data-testid="new-cust-gstin" value={f.gstin} onChange={(e) => setF({ ...f, gstin: e.target.value })} className="rounded-sm" />
        </div>
        <div>
          <label className="label-caps block mb-1.5">Phone</label>
          <Input data-testid="new-cust-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="rounded-sm" />
        </div>
        <div>
          <label className="label-caps block mb-1.5">Contact Person</label>
          <Input data-testid="new-cust-contact" value={f.contact_person} onChange={(e) => setF({ ...f, contact_person: e.target.value })} className="rounded-sm" />
        </div>
        <div>
          <label className="label-caps block mb-1.5">Email</label>
          <Input data-testid="new-cust-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="rounded-sm" />
        </div>
        <div className="col-span-2">
          <label className="label-caps block mb-1.5">Corporate / Registered Address</label>
          <textarea
            data-testid="new-cust-corp-addr"
            value={f.corporate_address}
            onChange={(e) => setF({ ...f, corporate_address: e.target.value })}
            className="w-full rounded-sm border border-neutral-200 px-3 py-2 text-sm"
            rows={2}
            placeholder="HQ / GSTIN registered address for invoicing"
          />
        </div>
        <div className="col-span-2">
          <label className="label-caps block mb-1.5">Delivery / Shipping Address</label>
          <textarea
            data-testid="new-cust-deliv-addr"
            value={f.delivery_address}
            onChange={(e) => setF({ ...f, delivery_address: e.target.value })}
            className="w-full rounded-sm border border-neutral-200 px-3 py-2 text-sm"
            rows={2}
            placeholder="Manufacturing / warehouse address where goods are shipped"
          />
        </div>
        <div className="col-span-2">
          <label className="label-caps block mb-1.5">Notes</label>
          <Input data-testid="new-cust-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="rounded-sm" />
        </div>
        <DialogFooter className="col-span-2">
          <Button data-testid="new-cust-submit" type="submit" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
            {isEdit ? "Save Changes" : "Add Customer"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function RevenueViewLoader({ defaultQ }) {
  const [mode, setMode] = useState("summary"); // summary | compare | churn
  return (
    <Tabs value={mode} onValueChange={setMode} className="w-full">
      <TabsList className="rounded-sm bg-neutral-100 border border-neutral-200 mb-3">
        <TabsTrigger data-testid="rev-tab-summary" value="summary" className="rounded-sm">Summary</TabsTrigger>
        <TabsTrigger data-testid="rev-tab-compare" value="compare" className="rounded-sm">Compare Periods</TabsTrigger>
        <TabsTrigger data-testid="rev-tab-churn" value="churn" className="rounded-sm">Churn / Dropped</TabsTrigger>
      </TabsList>
      <TabsContent value="summary" className="mt-0"><SummaryPanel defaultQ={defaultQ} /></TabsContent>
      <TabsContent value="compare" className="mt-0"><ComparePanel /></TabsContent>
      <TabsContent value="churn" className="mt-0"><ChurnPanel /></TabsContent>
    </Tabs>
  );
}

// ---------------- Summary panel with date range + Excel export ----------------
function SummaryPanel({ defaultQ }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q] = useState(defaultQ || "");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [limit, setLimit] = useState(200);

  const buildParams = () => {
    const p = { limit };
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  };

  const load = () => {
    setBusy(true); setErr(null);
    api.get("/sales/by-customer", { params: buildParams() })
      .then(({ data }) => setRows(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load revenue"))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportXlsx = async () => {
    try {
      const res = await api.get("/sales/by-customer/export", { params: buildParams(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `revenue-by-customer_${dateFrom || "all"}_to_${dateTo || "today"}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const applyPreset = (preset) => {
    const now = new Date();
    let from, to = now.toISOString().slice(0, 10);
    if (preset === "this_month") from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    else if (preset === "last_month") {
      from = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().slice(0, 10);
      to = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().slice(0, 10);
    } else if (preset === "fytd") {
      const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
      from = `${y}-04-01`;
    } else if (preset === "last_fy") {
      const y = now.getMonth() >= 3 ? now.getFullYear() - 1 : now.getFullYear() - 2;
      from = `${y}-04-01`; to = `${y + 1}-03-31`;
    } else if (preset === "cy") from = `${now.getFullYear()}-01-01`;
    else if (preset === "all") { from = ""; to = ""; }
    setDateFrom(from || ""); setDateTo(to || "");
  };

  return (
    <div>
      <div className="border border-neutral-200 bg-white mb-3 p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="label-caps block mb-1">From date</label>
          <Input data-testid="rev-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-sm w-44" />
        </div>
        <div>
          <label className="label-caps block mb-1">To date</label>
          <Input data-testid="rev-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-sm w-44" />
        </div>
        <div>
          <label className="label-caps block mb-1">Top N</label>
          <Input data-testid="rev-limit" type="number" min="10" max="1000" value={limit} onChange={(e) => setLimit(Number(e.target.value) || 200)} className="rounded-sm w-24" />
        </div>
        <Button data-testid="rev-apply" onClick={load} disabled={busy} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
          {busy ? "Loading…" : "Apply"}
        </Button>
        <Button data-testid="rev-export" onClick={exportXlsx} variant="outline" className="rounded-sm">
          Download .xlsx
        </Button>
        <div className="flex flex-wrap gap-1.5 ml-auto text-[11px]">
          {[["This Month", "this_month"], ["Last Month", "last_month"], ["FY-to-Date", "fytd"], ["Last FY", "last_fy"], ["CY", "cy"], ["All Time", "all"]].map(([lbl, key]) => (
            <button key={key} data-testid={`rev-preset-${key}`} type="button" onClick={() => applyPreset(key)}
              className="px-2 py-1 rounded-sm border border-neutral-200 hover:bg-neutral-50 hover:border-neutral-400 transition-colors uppercase tracking-widest">
              {lbl}
            </button>
          ))}
        </div>
      </div>

      {(dateFrom || dateTo) && (
        <div className="mb-3 text-xs text-neutral-600">
          Showing revenue for
          <span className="mx-1.5 font-mono-num font-semibold text-neutral-900">{dateFrom || "beginning"}</span>→
          <span className="ml-1.5 font-mono-num font-semibold text-neutral-900">{dateTo || "today"}</span>
        </div>
      )}
      {err && <div className="border border-neutral-200 bg-white p-10 text-center text-sm text-rose-700">{err}</div>}
      {!err && rows === null && <div className="p-10 text-center text-neutral-400 label-caps">Loading revenue…</div>}
      {!err && rows !== null && <RevenueView byCustomer={rows} q={q} />}
    </div>
  );
}

// ---------------- Compare panel ----------------
function ComparePanel() {
  const now = new Date();
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().slice(0, 10);
  const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().slice(0, 10);
  const today = now.toISOString().slice(0, 10);

  const [aFrom, setAFrom] = useState(lastMonthStart);
  const [aTo, setATo] = useState(lastMonthEnd);
  const [bFrom, setBFrom] = useState(thisMonthStart);
  const [bTo, setBTo] = useState(today);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setBusy(true);
    api.get("/sales/by-customer/compare", { params: { a_from: aFrom, a_to: aTo, b_from: bFrom, b_to: bTo, limit: 500 } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div>
      <div className="border border-neutral-200 bg-white mb-3 p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-neutral-200 p-3">
          <div className="label-caps mb-2">Period A (baseline)</div>
          <div className="flex gap-2">
            <Input data-testid="cmp-a-from" type="date" value={aFrom} onChange={(e) => setAFrom(e.target.value)} className="rounded-sm" />
            <Input data-testid="cmp-a-to" type="date" value={aTo} onChange={(e) => setATo(e.target.value)} className="rounded-sm" />
          </div>
        </div>
        <div className="border border-neutral-200 p-3">
          <div className="label-caps mb-2">Period B (compare against)</div>
          <div className="flex gap-2">
            <Input data-testid="cmp-b-from" type="date" value={bFrom} onChange={(e) => setBFrom(e.target.value)} className="rounded-sm" />
            <Input data-testid="cmp-b-to" type="date" value={bTo} onChange={(e) => setBTo(e.target.value)} className="rounded-sm" />
          </div>
        </div>
        <div className="col-span-1 md:col-span-2 flex justify-end gap-2">
          <Button
            data-testid="cmp-export"
            variant="outline"
            className="rounded-sm"
            onClick={async () => {
              try {
                const res = await api.get("/sales/by-customer/compare/export", {
                  params: { a_from: aFrom, a_to: aTo, b_from: bFrom, b_to: bTo, limit: 500 },
                  responseType: "blob",
                });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url; a.download = `revenue-compare_A=${aFrom || "all"}_B=${bFrom || "all"}.xlsx`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                toast.success("Excel downloaded");
              } catch (e) { toast.error(formatApiError(e)); }
            }}
          >
            Download .xlsx
          </Button>
          <Button data-testid="cmp-apply" onClick={load} disabled={busy} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
            {busy ? "Loading…" : "Compare"}
          </Button>
        </div>
      </div>
      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <SumCard label={`Period A · ${data.period_a.from || "beginning"} → ${data.period_a.to || "today"}`}
              revenue={data.period_a.total_revenue} invoices={data.period_a.total_invoices} tone="neutral" />
            <SumCard label={`Period B · ${data.period_b.from || "beginning"} → ${data.period_b.to || "today"}`}
              revenue={data.period_b.total_revenue} invoices={data.period_b.total_invoices} tone={data.period_b.total_revenue >= data.period_a.total_revenue ? "up" : "down"}
              delta={data.period_b.total_revenue - data.period_a.total_revenue} />
          </div>
          <div className="border border-neutral-200 bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="compare-table">
              <thead className="bg-neutral-50 label-caps">
                <tr>
                  <th className="text-left px-4 py-2.5">Customer</th>
                  <th className="text-right px-4 py-2.5">A Revenue</th>
                  <th className="text-right px-4 py-2.5">A Inv</th>
                  <th className="text-right px-4 py-2.5">B Revenue</th>
                  <th className="text-right px-4 py-2.5">B Inv</th>
                  <th className="text-right px-4 py-2.5">Δ Revenue</th>
                  <th className="text-right px-4 py-2.5">Δ %</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => {
                  const isUp = r.delta > 0, isDown = r.delta < 0;
                  return (
                    <tr key={r.party_name} className="border-t border-neutral-100 data-row">
                      <td className="px-4 py-2.5 font-medium">{r.party_name}</td>
                      <td className="px-4 py-2.5 text-right font-mono-num text-neutral-700">{formatINR(r.revenue_a)}</td>
                      <td className="px-4 py-2.5 text-right font-mono-num text-xs text-neutral-500">{r.invoices_a}</td>
                      <td className="px-4 py-2.5 text-right font-mono-num text-neutral-900 font-semibold">{formatINR(r.revenue_b)}</td>
                      <td className="px-4 py-2.5 text-right font-mono-num text-xs text-neutral-500">{r.invoices_b}</td>
                      <td className={`px-4 py-2.5 text-right font-mono-num font-semibold ${isUp ? "text-emerald-700" : isDown ? "text-rose-700" : "text-neutral-400"}`}>
                        {isUp && "+"}{formatINR(r.delta)}
                      </td>
                      <td className={`px-4 py-2.5 text-right font-mono-num ${isUp ? "text-emerald-700" : isDown ? "text-rose-700" : "text-neutral-400"}`}>
                        {isUp && "+"}{r.pct_change}%
                      </td>
                    </tr>
                  );
                })}
                {data.rows.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-neutral-400">No data in the selected periods.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------- Churn panel ----------------
function ChurnPanel() {
  const now = new Date();
  const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const [baseFrom, setBaseFrom] = useState(`${y - 1}-04-01`);
  const [baseTo, setBaseTo] = useState(`${y}-03-31`);
  const [currFrom, setCurrFrom] = useState(`${y}-04-01`);
  const [currTo, setCurrTo] = useState(now.toISOString().slice(0, 10));
  const [threshold, setThreshold] = useState(50); // % retained cutoff
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setBusy(true);
    api.get("/sales/by-customer/churn", { params: {
      baseline_from: baseFrom, baseline_to: baseTo,
      current_from: currFrom, current_to: currTo,
      drop_threshold: threshold, limit: 500,
    }})
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div>
      <div className="border border-neutral-200 bg-white mb-3 p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-neutral-200 p-3">
          <div className="label-caps mb-2">Baseline period</div>
          <div className="flex gap-2">
            <Input data-testid="churn-base-from" type="date" value={baseFrom} onChange={(e) => setBaseFrom(e.target.value)} className="rounded-sm" />
            <Input data-testid="churn-base-to" type="date" value={baseTo} onChange={(e) => setBaseTo(e.target.value)} className="rounded-sm" />
          </div>
        </div>
        <div className="border border-neutral-200 p-3">
          <div className="label-caps mb-2">Current period</div>
          <div className="flex gap-2">
            <Input data-testid="churn-curr-from" type="date" value={currFrom} onChange={(e) => setCurrFrom(e.target.value)} className="rounded-sm" />
            <Input data-testid="churn-curr-to" type="date" value={currTo} onChange={(e) => setCurrTo(e.target.value)} className="rounded-sm" />
          </div>
        </div>
        <div className="col-span-1 md:col-span-2 flex items-end gap-4">
          <div>
            <label className="label-caps block mb-1">Show if retained %</label>
            <Input data-testid="churn-threshold" type="number" min="0" max="100" value={threshold} onChange={(e) => setThreshold(Number(e.target.value))} className="rounded-sm w-32" />
            <div className="text-[11px] text-neutral-500 mt-1">0% = fully dropped · 50% = dropped by half</div>
          </div>
          <div className="ml-auto flex gap-2">
          <Button
            data-testid="churn-export"
            variant="outline"
            className="rounded-sm"
            onClick={async () => {
              try {
                const res = await api.get("/sales/by-customer/churn/export", {
                  params: {
                    baseline_from: baseFrom, baseline_to: baseTo,
                    current_from: currFrom, current_to: currTo,
                    drop_threshold: threshold, limit: 500,
                  },
                  responseType: "blob",
                });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url; a.download = `churn-report_${baseFrom || "all"}_vs_${currFrom || "today"}.xlsx`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                toast.success("Excel downloaded");
              } catch (e) { toast.error(formatApiError(e)); }
            }}
          >
            Download .xlsx
          </Button>
          <Button data-testid="churn-apply" onClick={load} disabled={busy} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
            {busy ? "Loading…" : "Analyze"}
          </Button>
          </div>
        </div>
      </div>
      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <SumCard label="Churned customers" revenue={data.churned_customers} isCount />
            <SumCard label="Revenue lost" revenue={data.total_revenue_lost} tone="down" />
            <SumCard label={`Baseline (${data.baseline.customer_count} customers)`} revenue={data.baseline.total_revenue} />
          </div>
          <div className="border border-neutral-200 bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="churn-table">
              <thead className="bg-neutral-50 label-caps">
                <tr>
                  <th className="text-left px-4 py-2.5">Customer</th>
                  <th className="text-right px-4 py-2.5">Baseline Rev</th>
                  <th className="text-right px-4 py-2.5">Baseline Inv</th>
                  <th className="text-right px-4 py-2.5">Current Rev</th>
                  <th className="text-right px-4 py-2.5">Current Inv</th>
                  <th className="text-right px-4 py-2.5">Revenue Lost</th>
                  <th className="text-right px-4 py-2.5">Retained %</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.party_name} className="border-t border-neutral-100 data-row">
                    <td className="px-4 py-2.5 font-medium">{r.party_name}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num">{formatINR(r.baseline_revenue)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num text-xs text-neutral-500">{r.baseline_invoices}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num text-neutral-700">{formatINR(r.current_revenue)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num text-xs text-neutral-500">{r.current_invoices}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num font-semibold text-rose-700">{formatINR(r.revenue_lost)}</td>
                    <td className={`px-4 py-2.5 text-right font-mono-num font-semibold ${
                      r.pct_retained === 0 ? "text-rose-700" : r.pct_retained < 25 ? "text-amber-700" : "text-neutral-900"
                    }`}>
                      {r.pct_retained}%
                    </td>
                  </tr>
                ))}
                {data.rows.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-neutral-400">No customers churned in this window 🎉</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function SumCard({ label, revenue, invoices, tone, delta, isCount }) {
  const toneCls =
    tone === "up" ? "border-emerald-300 bg-emerald-50" :
    tone === "down" ? "border-rose-300 bg-rose-50" :
    "border-neutral-200 bg-white";
  return (
    <div className={`border ${toneCls} px-4 py-3`}>
      <div className="label-caps mb-1 text-[10px]">{label}</div>
      <div className="flex items-baseline gap-3">
        <div className="font-mono-num text-2xl font-bold text-neutral-900" title={isCount ? undefined : formatINR(revenue)}>
          {isCount ? Number(revenue).toLocaleString("en-IN") : formatINRShort(revenue)}
        </div>
        {typeof invoices === "number" && (
          <div className="text-xs text-neutral-500 font-mono-num">{invoices.toLocaleString("en-IN")} inv</div>
        )}
        {typeof delta === "number" && (
          <div className={`text-xs font-mono-num font-semibold ${delta > 0 ? "text-emerald-700" : delta < 0 ? "text-rose-700" : "text-neutral-400"}`}>
            {delta > 0 ? "+" : ""}{formatINRShort(delta)}
          </div>
        )}
      </div>
    </div>
  );
}

function RevenueView({ byCustomer, q }) {
  const filtered = q
    ? byCustomer.filter((c) => c.party_name.toLowerCase().includes(q.toLowerCase()))
    : byCustomer;

  // Group by parent brand
  const parentGroups = {};
  filtered.forEach((c) => {
    const parts = c.party_name.split(/[-·]|Unit-/i);
    const parent = parts[0].replace(/\b(Ltd|Pvt|Private|Limited|Pharma)\.?\b/gi, "").trim() || c.party_name;
    if (!parentGroups[parent]) parentGroups[parent] = { name: parent, units: [], total_rev: 0, total_inv: 0 };
    parentGroups[parent].units.push(c);
    parentGroups[parent].total_rev += c.revenue;
    parentGroups[parent].total_inv += c.invoices;
  });
  const parentList = Object.values(parentGroups).sort((a, b) => b.total_rev - a.total_rev);

  return (
    <div className="border border-neutral-200 bg-white">
      <div className="px-5 py-4 border-b border-neutral-200 flex justify-between items-center">
        <div className="label-caps">Customer Groups & Manufacturing Units</div>
        <div className="text-xs text-neutral-500">
          <span className="font-mono-num font-bold text-neutral-900">{parentList.length}</span> groups ·{" "}
          <span className="font-mono-num font-bold text-neutral-900">{filtered.length}</span> units
        </div>
      </div>
      <div className="divide-y divide-neutral-100">
        {parentList.map((g) => <ParentRow key={g.name} group={g} />)}
        {parentList.length === 0 && (
          <div className="px-5 py-10 text-center text-neutral-400 text-sm">
            No sales data yet. Import a Sales file or start creating jobs to see revenue by customer.
          </div>
        )}
      </div>
    </div>
  );
}

function ParentRow({ group }) {
  const [openRow, setOpenRow] = useState(group.units.length <= 3);
  return (
    <div>
      <button
        onClick={() => setOpenRow(!openRow)}
        className="w-full px-5 py-3 flex items-center justify-between hover:bg-neutral-50 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          {openRow ? <ChevronDown size={14} className="text-neutral-500" /> : <ChevronRight size={14} className="text-neutral-500" />}
          <Building2 size={14} className="text-[#FF5A00]" />
          <span className="font-medium text-sm">{group.name}</span>
          <span className="text-xs text-neutral-500 ml-1">· {group.units.length} unit{group.units.length !== 1 ? "s" : ""}</span>
        </div>
        <div className="flex items-center gap-6 text-sm">
          <span className="font-mono-num text-neutral-600">{group.total_inv.toLocaleString("en-IN")} inv</span>
          <span className="font-mono-num font-bold w-32 text-right">{formatINR(group.total_rev)}</span>
        </div>
      </button>
      {openRow && (
        <div className="bg-neutral-50/50 border-t border-neutral-100 px-5 py-2">
          {group.units.map((u) => (
            <div key={u.party_name} className="flex justify-between items-center py-1.5 pl-6">
              <div className="text-sm text-neutral-700">{u.party_name}</div>
              <div className="flex items-center gap-6 text-xs">
                <span className="font-mono-num text-neutral-500">{u.invoices} inv</span>
                <span className="font-mono-num w-32 text-right text-neutral-900">{formatINR(u.revenue)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
